import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SettingsState } from '@/types/store';
import logger, { LogLevel } from '@/services/logger';

const initialState: SettingsState = {
  apiKey: '',
  apiKeyUrlParam: '',
  apiEndpoint: 'https://api.openai.com/v1/chat/completions',
  model: 'gpt-4',
  maxTokens: 4000,
  temperature: 0.7,
  showAdvancedOptions: false,
  autoSave: true,
  logLevel: 'info',
};

// Check URL for API key parameter
const getApiKeyFromUrl = (): string | undefined => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('apiKey') || undefined;
  }
  return undefined;
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    ...initialState,
    apiKeyUrlParam: getApiKeyFromUrl(),
  },
  reducers: {
    setApiKey: (state, action: PayloadAction<string>) => {
      state.apiKey = action.payload;
      logger.info('API key updated', { module: 'settings' });
    },
    
    setApiEndpoint: (state, action: PayloadAction<string>) => {
      state.apiEndpoint = action.payload;
    },
    
    setModel: (state, action: PayloadAction<string>) => {
      state.model = action.payload;
    },
    
    setMaxTokens: (state, action: PayloadAction<number>) => {
      state.maxTokens = action.payload;
    },
    
    setTemperature: (state, action: PayloadAction<number>) => {
      state.temperature = action.payload;
    },
    
    toggleAdvancedOptions: (state) => {
      state.showAdvancedOptions = !state.showAdvancedOptions;
    },
    
    setAutoSave: (state, action: PayloadAction<boolean>) => {
      state.autoSave = action.payload;
    },
    
    setLogLevel: (state, action: PayloadAction<'debug' | 'info' | 'warn' | 'error'>) => {
      state.logLevel = action.payload;
      // Convert string log level to LogLevel enum
      const logLevelMap: Record<string, LogLevel> = {
        debug: LogLevel.DEBUG,
        info: LogLevel.INFO,
        warn: LogLevel.WARN,
        error: LogLevel.ERROR,
      };
      logger.configure({ level: logLevelMap[action.payload] });
      logger.info(`Log level set to ${action.payload}`, { module: 'settings' });
    },
    
    resetSettings: (state) => {
      return {
        ...initialState,
        // Preserve API key from URL parameter if it exists
        apiKeyUrlParam: state.apiKeyUrlParam,
      };
    },
  },
});

export const {
  setApiKey,
  setApiEndpoint,
  setModel,
  setMaxTokens,
  setTemperature,
  toggleAdvancedOptions,
  setAutoSave,
  setLogLevel,
  resetSettings,
} = settingsSlice.actions;

export default settingsSlice.reducer; 